import { css } from '@emotion/react';
import { LinearProgress, Paper, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import EnhancedTable, { PAGE_SIZE_OPTIONS } from '../WorkflowRunsPage/Table/EnhancedTable';
import FilterBar, {
  SORT_OPTIONS,
  STATE_FILTERS,
  StateFilter,
  TASK_STATE_FILTERS,
  TaskStateFilterValue,
  nextStateFilter,
  nextTaskStateFilter,
} from '../WorkflowRunsPage/FilterBar';
import ErrorSummaryDialog from './ErrorSummaryDialog';
import TaskCountBreakdown from './TaskCountBreakdown';
import { useDashboardColumns } from './columns';

import useAutofetchedDashboardRunsAsync from 'store/useAutofetchedDashboardRunsAsync';
import useAutofetchedDashboardTaskCountsAsync from 'store/useAutofetchedDashboardTaskCountsAsync';
import useDashboardUsersAsync from 'store/useDashboardUsersAsync';
import useProjectsAsync from 'store/useProjectsAsync';
import fetchDashboardRuns, {
  DashboardSortKey,
  DashboardSortOrder,
  IDashboardRunsParams,
} from 'store/fetchDashboardRuns';
import useDebouncedSearchInput from 'hooks/useDebouncedSearchInput';
import useListStateParams, {
  booleanParam,
  oneOfParam,
  positiveIntParam,
  stringListParam,
  stringParam,
} from 'hooks/useListStateParams';

import { ACTIVITY_WINDOWS, ActivityWindow, DEFAULT_ACTIVITY_WINDOW } from 'types/RunState';
import IProject from 'types/IProject';
import IRunEntry from 'types/IRunEntry';

const listParams = {
  page: positiveIntParam(1),
  pageSize: oneOfParam(PAGE_SIZE_OPTIONS, 25),
  search: stringParam(),
  projects: stringListParam(),
  state: oneOfParam(STATE_FILTERS, ''),
  taskState: oneOfParam(TASK_STATE_FILTERS, ''),
  activityWindow: oneOfParam(ACTIVITY_WINDOWS, DEFAULT_ACTIVITY_WINDOW),
  user: stringParam(),
  running: booleanParam(),
  sort: oneOfParam(
    SORT_OPTIONS.map((option) => option.key),
    'created_on',
  ),
  order: oneOfParam<DashboardSortOrder>(['asc', 'desc'], 'desc'),
};

export default function Dashboard() {
  const { state, update } = useListStateParams(listParams);
  const [errorSummaryRun, setErrorSummaryRun] = useState<IRunEntry | null>(null);

  const applySearch = useCallback((value: string) => update({ search: value, page: 1 }), [update]);
  const [search, handleSearchChange] = useDebouncedSearchInput(state.search, applySearch);

  const projectsAsync = useProjectsAsync();
  const projects =
    projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : undefined;
  const selectedProjects = useMemo(
    () => (projects ?? []).filter((project) => state.projects.includes(project.id)),
    [projects, state.projects],
  );

  const handleProjectsChange = useCallback(
    (selected: IProject[]) => update({ projects: selected.map((project) => project.id), page: 1 }),
    [update],
  );

  const handleUserIdChange = useCallback((id: string) => update({ user: id, page: 1 }), [update]);

  const handleHasRunningTasksChange = useCallback((value: boolean) => update({ running: value, page: 1 }), [update]);

  const handleStateClick = useCallback(
    (value: string) => {
      const next = nextStateFilter(state.state, value);
      if (next === null) return;
      update({ state: next, page: 1 });
    },
    [state.state, update],
  );

  const handleTaskStateClick = useCallback(
    (value: string) => {
      const next = nextTaskStateFilter(state.taskState, value);
      if (next === null) return;
      update({ taskState: next, page: 1 });
    },
    [state.taskState, update],
  );

  const handleSortChange = useCallback((key: DashboardSortKey) => update({ sort: key, page: 1 }), [update]);

  const handleOrderChange = useCallback((value: DashboardSortOrder) => update({ order: value, page: 1 }), [update]);

  const usersAsync = useDashboardUsersAsync();
  const users = useMemo(
    () =>
      (usersAsync.status === 'SUCCESS' || usersAsync.status === 'REFRESH' ? (usersAsync.data ?? []) : []).filter(
        (user): user is typeof user & { id: string } => !!user.id,
      ),
    [usersAsync],
  );

  const runsParams = useMemo<IDashboardRunsParams>(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      search: state.search || undefined,
      projectIds: state.projects.length > 0 ? state.projects : undefined,
      userId: state.user || undefined,
      state: state.state || undefined,
      hasRunningTasks: state.running || undefined,
      taskState: state.taskState || undefined,
      updatedWithinDays: state.activityWindow || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [state],
  );
  const runsAsync = useAutofetchedDashboardRunsAsync(runsParams);
  const refetchRuns = useCallback(() => fetchDashboardRuns(runsParams), [runsParams]);
  const taskCountsAsync = useAutofetchedDashboardTaskCountsAsync({
    projectIds: runsParams.projectIds,
    userId: runsParams.userId,
    updatedWithinDays: runsParams.updatedWithinDays,
  });
  const taskCounts =
    taskCountsAsync.status === 'SUCCESS' || taskCountsAsync.status === 'REFRESH'
      ? taskCountsAsync.data.counts
      : undefined;

  const { status } = runsAsync;
  const paginatedData = runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH' ? runsAsync.data : undefined;
  const runs = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;

  const columns = useDashboardColumns(setErrorSummaryRun, {
    onStateClick: handleStateClick,
    selectedState: state.state,
  });

  const handlePageChange = useCallback((newPage: number) => update({ page: newPage }), [update]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => update({ pageSize: newPageSize, page: 1 }),
    [update],
  );

  return (
    <section>
      <Paper elevation={1} style={{ padding: 48 }}>
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 8px 16px;
            margin-bottom: 16px;
          `}
        >
          <Typography component="h1" variant="h6">
            Dashboard
          </Typography>
          {taskCounts && (
            <TaskCountBreakdown
              counts={taskCounts}
              selectedState={state.taskState}
              onSelectState={handleTaskStateClick}
            />
          )}
        </div>
        {status === 'ERROR' && <p>Something went wrong</p>}
        <FilterBar
          searchId="search-dashboard-workflows"
          search={search}
          onSearchChange={handleSearchChange}
          state={state.state}
          onStateChange={(value: StateFilter) => update({ state: value, page: 1 })}
          taskState={state.taskState}
          onTaskStateChange={(value: TaskStateFilterValue) => update({ taskState: value, page: 1 })}
          activityWindow={state.activityWindow}
          onActivityWindowChange={(value: ActivityWindow) => update({ activityWindow: value, page: 1 })}
          users={users}
          userId={state.user}
          onUserIdChange={handleUserIdChange}
          hasRunningTasks={state.running}
          onHasRunningTasksChange={handleHasRunningTasksChange}
          sort={state.sort}
          onSortChange={handleSortChange}
          order={state.order}
          onOrderChange={handleOrderChange}
          projects={{
            options: projects ?? [],
            selected: selectedProjects,
            onChange: handleProjectsChange,
          }}
        />
        <br />
        {['INIT', 'PENDING'].includes(status) && <LinearProgress />}
        <EnhancedTable<IRunEntry>
          columns={columns}
          items={runs}
          serverPagination={{
            page: state.page,
            pageSize: state.pageSize,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
      </Paper>
      {errorSummaryRun && (
        <ErrorSummaryDialog
          run={errorSummaryRun}
          onClose={() => setErrorSummaryRun(null)}
          onResetSuccess={refetchRuns}
        />
      )}
    </section>
  );
}
